import {
  Deal,
  PackageConfig,
  PackageVariant,
  PriceValue,
  StrapiID,
} from '@ca/common/types'

import useRWD from '@ui/theme/useRWD'
import Page from '@shell/Page'
import ConfigDebugModal from 'features/debug/ConfigDebugModal'
import Header from '@ui/elements/Header'
import VideoContainer from '@/common/Video/VideoContainer'
import DealsModal from '@order/modals/Deals/DealsModal'
import useBundleType from '@order/hooks/useBundleType'
import { useDeal } from '@order/hooks/useDeal'
import { useCustomDealSettings } from '@order/hooks/useDealSettings'
import {
  usePackages,
  usePackageUpgrades,
  usePackageVariant,
} from '@order/hooks/usePackages'
import useStartOrderConfig from '@order/hooks/useStartOrderConfig'
import { Discounts } from '@order/utils/discounts/Discounts'
import OptionDefault from '@order/steps/templates/Default/Option/OptionDefault'
import { sDefaultTemplate } from '@order/steps/templates/Default/styles'
import { useNavigation } from 'expo-router'
import { useRemoteConfig } from 'hooks/useRemoteConfigStore'
import useSession, {
  DealContextKey,
  getSessionDealContext,
} from 'hooks/useSession'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { View } from 'react-native'

const useUpgradePrice = (
  deal?: Deal,
  pckg?: PackageConfig | PackageVariant
) => {
  const config = useRemoteConfig()

  const { activePackage, basePackage } = usePackages()
  const currentPckg = activePackage ?? basePackage
  const settings = useCustomDealSettings(deal)

  if (!pckg || !deal) {
    return Discounts.wrap(0)
  }
  if (!settings.upsellShowDiff) {
    return Discounts.wrapRegion(pckg.price, config?.region)
  }
  const currentPrice = PriceValue.safe(currentPckg?.price, config?.region)
  const priceValue = PriceValue.safe(pckg.price, config?.region)
  const toPay = priceValue - currentPrice
  const price = Discounts.wrap(toPay)

  return price
}

const OptionPackage = ({
  packages,
  deal,
  toPackages,
  dealUpgradeKey,
}: {
  dealUpgradeKey: DealContextKey
  packages: Record<string, PackageConfig>
  deal?: Deal
  toPackages: ReturnType<typeof useStartOrderConfig>['toPackages']
}) => {
  const { t } = useTranslation(['configuration'])
  const text = 'configuration:upsell'

  const upgrade = useMemo(() => {
    const id = deal?.packages.find((id) => packages[id]?.type === 'vf')
    if (!id) {
      return undefined
    }
    return packages[id]
  }, [packages, deal])

  const variant = usePackageVariant(upgrade)
  const pckg = variant || upgrade
  const price = useUpgradePrice(deal, pckg)

  if (!pckg || !deal) {
    return null
  }

  const onPress = () => {
    toPackages(deal, upgrade)
  }

  return (
    <OptionDefault
      id={2}
      onPress={onPress}
      title={t(`${text}.options.upgrade.title`)}
      desc={t(`${text}.options.upgrade.desc`)}
      options={2}
      price={price}
    />
  )
}

const OptionDeal = ({
  packages,
  dealId,
  toPackages,
  dealUpgradeKey,
}: {
  dealUpgradeKey: DealContextKey
  packages: Record<string, PackageConfig>
  dealId?: StrapiID
  toPackages: ReturnType<typeof useStartOrderConfig>['toPackages']
}) => {
  const { t } = useTranslation(['configuration'])
  const text = 'configuration:upsell'
  const deal = useDeal(dealId)
  const setDeal = useSession((s) => s.setDeal)

  const upgrade = useMemo(() => {
    const id = deal?.packages.find((id) => packages[id]?.type === 'vf')
    if (!id) {
      return undefined
    }
    return packages[id]
  }, [packages, deal])

  const variant = usePackageVariant(upgrade)
  const pckg = variant || upgrade
  const price = useUpgradePrice(deal, pckg)
  if (!pckg || !deal) {
    return null
  }

  const onPress = () => {
    setDeal(
      getSessionDealContext(deal, {
        basePackageId: pckg.id,
      }),
      dealUpgradeKey
    )

    toPackages(deal, upgrade)
  }

  return (
    <OptionDefault
      id={2}
      onPress={onPress}
      title={t(`${text}.options.upgrade.title`)}
      desc={t(`${text}.options.upgrade.desc`)}
      options={2}
      price={price}
    />
  )
}

export default function UpsellStep() {
  const { video } = useRWD()
  const { t } = useTranslation(['video', 'configuration'])
  const text = 'configuration:upsell'

  const { selectedDeal, packages, baseDeal, basePackage, dealUpgrade } =
    usePackageUpgrades()
  const { toPackages } = useStartOrderConfig()
  const [dealModal, setDealModal] = useState(!selectedDeal)
  const upsellType = selectedDeal?.vfDeal ? 'deal' : 'package'
  const { dealUpgradeKey } = useBundleType()
  const setDeal = useSession((s) => s.setDeal)

  const navigation = useNavigation()

  useEffect(() => {
    if (!dealUpgrade) {
      return
    }
    // On screen focus
    const unsubscribeFocus = navigation.addListener('focus', () =>
      setDeal(undefined, dealUpgradeKey)
    )

    return () => {
      unsubscribeFocus()
    }
  }, [navigation, dealUpgradeKey, dealUpgrade])

  return (
    <Page stage="configuration">
      <Header
        title={t(`${text}.header.title`)}
        desc={t(`${text}.header.desc`)}
      />
      {!selectedDeal && (
        <DealsModal open={dealModal} close={() => setDealModal(false)} />
      )}

      <View className="w-full px-8 pb-8">
        <View style={video}>
          <VideoContainer autoplay url={t('video:upsell-varifocals')} />
        </View>
      </View>

      <View className={sDefaultTemplate}>
        <OptionDefault
          id={1}
          onPress={() => {
            // clear upgrade (to handle backs)
            setDeal(undefined, dealUpgradeKey)
            toPackages(baseDeal, basePackage ?? undefined)
          }}
          title={t(`${text}.options.skip.title`)}
          desc={t(`${text}.options.skip.desc`)}
          options={2}
          price={undefined}
        />
        {upsellType === 'package' && (
          <OptionPackage
            packages={packages}
            dealUpgradeKey={dealUpgradeKey}
            deal={selectedDeal}
            toPackages={toPackages}
          />
        )}
        {upsellType === 'deal' && baseDeal?.vfDeal && (
          <OptionDeal
            dealUpgradeKey={dealUpgradeKey}
            dealId={baseDeal?.vfDeal}
            toPackages={toPackages}
            packages={packages}
          />
        )}
      </View>
      <ConfigDebugModal />
    </Page>
  )
}
